/**
 * MyAttendancePage (v36 polish) — Personal attendance dashboard for ANY logged-in staff member
 * (Front Desk, Nurse, Doctor, Billing, HR, IT, Admin) with a hospital_id. Super Admin excluded.
 *
 * Renders:
 *  - Big "Today" card with check-in / check-out timestamps + Mark In / Mark Out action button
 *  - This-month KPIs (Present / Absent / Rate / Total hours)
 *  - Month calendar grid (clickable days) with check-in/out chips
 *  - Recent 14-day timeline list (chronological, latest first)
 *
 * Route: /my-attendance — open to every authenticated user with a hospital_id.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { attendanceAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Clock, LogIn, LogOut, CheckCircle2, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Timer,
} from 'lucide-react';
import { cn } from '../lib/utils';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (t) => (t || '').slice(0, 5);
const todayISO = () => new Date().toISOString().slice(0, 10);

const MyAttendancePage = () => {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // No-op for super admin / users without hospital
  const enabled = !!user && !user.is_super_admin && !!user.hospital_id;

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await attendanceAPI.getUserMonthly(user.id, { month, year });
      setRecords(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.id, month, year]);

  useEffect(() => { load(); }, [load]);

  const todayRow = useMemo(() => records.find(r => r.date === todayISO()) || null, [records]);
  const checkedIn = !!todayRow?.check_in;
  const checkedOut = !!todayRow?.check_out;

  const handleMarkIn = async () => {
    setSubmitting(true);
    try {
      const res = await attendanceAPI.markIn({});
      toast.success(`Checked in at ${fmt(res.data?.check_in)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark in');
    } finally { setSubmitting(false); }
  };

  const handleMarkOut = async () => {
    setSubmitting(true);
    try {
      const res = await attendanceAPI.markOut({});
      toast.success(`Checked out at ${fmt(res.data?.check_out)} · ${res.data?.work_hours}h logged`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark out');
    } finally { setSubmitting(false); }
  };

  // Calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();
  const todayDate = now.getDate();
  const attMap = useMemo(() => {
    const m = {};
    records.forEach(r => { m[r.date] = r; });
    return m;
  }, [records]);

  const presentDays = records.filter(r => r.status === 'present' || r.check_in).length;
  const expectedDays = isCurrentMonth ? todayDate : daysInMonth;
  const absentDays = Math.max(0, expectedDays - presentDays);
  const totalHours = records.reduce((s, r) => s + (r.work_hours || 0), 0).toFixed(1);
  const attendanceRate = expectedDays > 0 ? Math.round((presentDays / expectedDays) * 100) : 0;

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); setSelectedDay(null); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); setSelectedDay(null); };

  if (!enabled) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="my-attendance-disabled">
        Personal attendance is not available for this account type.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Attendance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Today card */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border border-teal-200 dark:border-teal-700/40 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm">
              <CalendarIcon className="text-teal-600 dark:text-teal-400" size={26} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-teal-700 dark:text-teal-300">Today</p>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
                {checkedOut ? 'Day Complete' : checkedIn ? 'Currently On Duty' : 'Not Yet Checked In'}
              </h2>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-600 dark:text-slate-400">
                {checkedIn && (
                  <span className="inline-flex items-center gap-1.5"><LogIn size={12} className="text-emerald-600" /> In at <b>{fmt(todayRow.check_in)}</b></span>
                )}
                {checkedOut && (
                  <span className="inline-flex items-center gap-1.5"><LogOut size={12} className="text-amber-600" /> Out at <b>{fmt(todayRow.check_out)}</b></span>
                )}
                {checkedOut && todayRow.work_hours > 0 && (
                  <span className="inline-flex items-center gap-1.5"><Timer size={12} className="text-blue-600" /> <b>{todayRow.work_hours}h</b> logged</span>
                )}
              </div>
            </div>
          </div>

          <div>
            {!checkedIn && (
              <Button onClick={handleMarkIn} disabled={submitting}
                className="h-11 px-6 gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-md"
                data-testid="my-attendance-page-mark-in">
                <LogIn size={16} /> Mark Check-In
              </Button>
            )}
            {checkedIn && !checkedOut && (
              <Button onClick={handleMarkOut} disabled={submitting}
                className="h-11 px-6 gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md"
                data-testid="my-attendance-page-mark-out">
                <LogOut size={16} /> Mark Check-Out
              </Button>
            )}
            {checkedOut && (
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/40 font-medium" data-testid="my-attendance-page-done">
                <CheckCircle2 size={16} /> Day Complete
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">Present</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="kpi-present">{presentDays}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">days this month</p>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">Absent</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="kpi-absent">{absentDays}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">expected: {expectedDays}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">Rate</p>
          <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="kpi-rate">{attendanceRate}%</p>
          <p className="text-[10px] text-slate-400 mt-0.5">attendance</p>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">Hours</p>
          <p className="text-2xl font-bold text-purple-600 mt-1" data-testid="kpi-hours">{totalHours}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">logged this month</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <button onClick={prevMonth} className="h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="my-att-prev-month">
            <ChevronLeft size={16} className="text-slate-600 dark:text-slate-300" />
          </button>
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-100" data-testid="my-att-current-month">{MONTHS[month - 1]} {year}</h2>
          <button onClick={nextMonth} className="h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="my-att-next-month">
            <ChevronRight size={16} className="text-slate-600 dark:text-slate-300" />
          </button>
        </div>

        <div className="grid grid-cols-7 px-3 pt-3 pb-1">
          {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-400 uppercase">{d}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-1 p-3">
          {Array.from({ length: firstDay }).map((_, i) => <div key={`pad-${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, idx) => {
            const day = idx + 1;
            const dStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const att = attMap[dStr];
            const isToday = isCurrentMonth && day === todayDate;
            const isFuture = isCurrentMonth && day > todayDate;
            const isSelected = selectedDay === day;
            return (
              <button
                key={day}
                onClick={() => setSelectedDay(s => s === day ? null : day)}
                disabled={isFuture && !att}
                className={cn(
                  'group h-16 rounded-lg border text-left p-1.5 transition-all hover:shadow-sm',
                  isSelected ? 'ring-2 ring-teal-500 border-teal-500' : 'border-slate-200 dark:border-slate-700',
                  isToday && 'bg-teal-50 dark:bg-teal-900/20 border-teal-300',
                  isFuture && !att && 'bg-slate-50 dark:bg-slate-800/40 opacity-50 cursor-not-allowed',
                  att?.check_in && !isToday && 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700/30',
                )}
                data-testid={`my-att-day-${day}`}>
                <div className="flex items-center justify-between">
                  <span className={cn('text-xs font-bold', isToday ? 'text-teal-700 dark:text-teal-400' : 'text-slate-700 dark:text-slate-300')}>{day}</span>
                  {att?.check_out && <CheckCircle2 size={10} className="text-emerald-600" />}
                </div>
                {att?.check_in && (
                  <p className="text-[9px] text-slate-500 dark:text-slate-400 font-mono mt-0.5 truncate">
                    {fmt(att.check_in)}{att.check_out ? `→${fmt(att.check_out)}` : ''}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay && (() => {
        const dStr = `${year}-${String(month).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        const r = attMap[dStr];
        return (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="my-att-day-detail">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">
              {MONTHS[month - 1]} {selectedDay}, {year}
            </p>
            {r ? (
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-xs text-slate-500">Check In</p><p className="font-bold text-emerald-600 mt-0.5">{fmt(r.check_in) || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Check Out</p><p className="font-bold text-amber-600 mt-0.5">{fmt(r.check_out) || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Hours</p><p className="font-bold text-blue-600 mt-0.5">{r.work_hours ?? '—'}h</p></div>
                <div><p className="text-xs text-slate-500">Status</p><p className="font-bold capitalize mt-0.5">{r.status || 'present'}</p></div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-2">No attendance recorded for this day.</p>
            )}
          </div>
        );
      })()}

      {/* Recent timeline */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Recent Activity</h3>
          <span className="text-[10px] text-slate-400">Last 14 days</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Check In</th>
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Check Out</th>
              <th className="text-center px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Hours</th>
              <th className="text-center px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !loading && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-xs">No attendance records yet. Click <b>Mark Check-In</b> above to start tracking.</td></tr>
            )}
            {records.slice(-14).reverse().map(r => (
              <tr key={r.id || r.date} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`my-att-row-${r.date}`}>
                <td className="px-5 py-2.5 text-xs text-slate-700 dark:text-slate-200 font-medium">{r.date}</td>
                <td className="px-5 py-2.5 text-xs font-mono text-emerald-600">{fmt(r.check_in) || '—'}</td>
                <td className="px-5 py-2.5 text-xs font-mono text-amber-600">{fmt(r.check_out) || '—'}</td>
                <td className="px-5 py-2.5 text-center text-xs font-bold text-blue-600">{r.work_hours ?? '—'}</td>
                <td className="px-5 py-2.5 text-center">
                  <span className={cn('text-[9px] px-2 py-0.5 rounded-full font-bold uppercase',
                    r.check_in && r.check_out ? 'bg-emerald-100 text-emerald-700' :
                    r.check_in ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-500'
                  )}>{r.check_in && r.check_out ? 'completed' : r.check_in ? 'in-progress' : (r.status || 'absent')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyAttendancePage;
